import { db } from "../../services/firebase";
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  DocumentData,
  doc,
  setDoc,
  updateDoc,
  arrayUnion,
  getDoc,
  arrayRemove,
  increment,
  deleteDoc,
} from "firebase/firestore";

const LEAGUE_COLLECTION = "leagueLocker";
const USER_COLLECTION = "users";
const FOLLOWER_COLLECTION = "leagueFollowers";
const LEAGUE_FEEDS_COLLECTION = "leagueLockerFeed";
const CHALLENGE_COLLECTION = "challenges";
const USER_LEAGUES_COLLECTION = "userLeagues";

export type Team = {
  createdBy: string | undefined;
  name: string;
  joinedAt: string;
  isDuePaid: boolean;
  imageUrl?: string;
  hasJoined?: boolean;
};

export interface LeagueFundsData {
  entryFee: number | null;
  isPunishmentFund: boolean;
  totalPot: number;
  distribution: { place: string; percentage: number; amount: number }[];
}

export const addLeagueToFirestore = async (leagueData: {
  leagueName: string;
  leagueUrl: string;
  leagueSize: string;
  platform: string;
  createdBy: string;
  isPublic: boolean;
  teams?: Team[];
  leagueCompletedSteps?: string[] | null;
}) => {
  try {
    const docRef = await addDoc(collection(db, LEAGUE_COLLECTION), leagueData);
    console.log("League added with ID:", docRef.id);
    return { id: docRef.id, ...leagueData };
  } catch (error) {
    console.error("Error adding league:", error);
    throw error;
  }
};

export const checkSlugExists = async (slug: string): Promise<boolean> => {
  const q = query(
    collection(db, LEAGUE_COLLECTION),
    where("leagueUrl", "==", slug)
  );
  const snapshot = await getDocs(q);
  return !snapshot.empty;
};

/**
 * Fetch a league by its slug (leagueUrl) along with its challenges subcollection
 */
export const getLeagueBySlug = async (
  leagueUrl: string
): Promise<DocumentData | null> => {
  try {
    const q = query(
      collection(db, LEAGUE_COLLECTION),
      where("leagueUrl", "==", leagueUrl)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.log("No league found for slug:", leagueUrl);
      return null;
    }

    const leagueDoc = snapshot.docs[0];
    const leagueData: DocumentData = {
      id: leagueDoc.id,
      ...leagueDoc.data(),
    };

    // Fetch and process challenges
    try {
      const challengesColRef = collection(
        db,
        LEAGUE_COLLECTION,
        leagueDoc.id,
        CHALLENGE_COLLECTION
      );
      const challengesSnapshot = await getDocs(challengesColRef);

      const rawChallenges: DocumentData[] = [];
      challengesSnapshot.forEach((challengeDoc) => {
        rawChallenges.push({ id: challengeDoc.id, ...challengeDoc.data() });
      });

      // Filter out deleted or expired
      const filtered = rawChallenges.filter(
        (c) =>
          !["deleted", "expired"].includes(
            (c.challengeCreationStatus || "").toLowerCase()
          )
      );

      // Sort by createdAt (descending). Handle missing or invalid dates.
      const getTime = (c: any) => {
        const v = c.createdAt;
        if (!v) return 0;
        const t = new Date(v).getTime();
        return isNaN(t) ? 0 : t;
      };

      filtered.sort((a, b) => getTime(b) - getTime(a));

      leagueData.challenges = filtered;
    } catch (challengeError) {
      console.log(
        "No challenges found or error fetching challenges for league:",
        leagueDoc.id
      );
      leagueData.challenges = [];
    }

    return leagueData;
  } catch (error) {
    console.error("Error fetching league by slug:", error);
    throw error;
  }
};

/**
 * Partially updates a league document with the 'leaguePunishment' key.
 * @param leagueId The Firestore document ID of the league.
 * @param punishmentData The object to set as 'leaguePunishment'.
 */
export const updateLeaguePunishment = async (
  leagueId: string,
  punishmentData: object
) => {
  try {
    const leagueDocRef = doc(db, LEAGUE_COLLECTION, leagueId);
    await updateDoc(leagueDocRef, { leaguePunishment: punishmentData });
    console.log("League punishment updated for ID:", leagueId);
    return true;
  } catch (error) {
    console.error("Error updating league punishment:", error);
  }
};

/* * Save league funds data to Firestore for a given league ID
 */
export const saveLeagueFunds = async (
  leagueId: string,
  leagueFunds: LeagueFundsData,
  team?: { name: string; createdBy: string; [key: string]: any } | {}
): Promise<void> => {
  try {
    await setDoc(
      doc(db, LEAGUE_COLLECTION, leagueId),
      { leagueFunds },
      { merge: true }
    );
    console.log("League funds saved:", leagueFunds);
  } catch (error) {
    console.error("Error saving league funds:", error);
    throw error;
  }
};

/**
 * Partially updates a league document with the 'leaguePunishment' key.
 * @param leagueId The Firestore document ID of the league.
 */
export const updateLeagueAdditionalDetails = async (
  leagueId: string,
  additionalDetails: object
) => {
  try {
    const leagueDocRef = doc(db, LEAGUE_COLLECTION, leagueId);
    await updateDoc(leagueDocRef, { additionalDetails });
    console.log("League punishment updated for ID:", leagueId);
    return true;
  } catch (error) {
    console.error("Error updating league punishment:", error);
  }
};

/**
 * Inline update function of League data about or rules
 */
/**
 * Partially update a single field in the league document.
 * @param leagueId The Firestore document ID of the league.
 * @param field The field name to update (e.g., 'about', 'rules').
 * @param value The value to set for the field.
 */
export const updateLeagueAboutOrRules = async (
  leagueId: string,
  field: string,
  value: any
) => {
  try {
    const leagueDocRef = doc(db, LEAGUE_COLLECTION, leagueId);
    await updateDoc(leagueDocRef, { [field]: value });
    console.log(`League field '${field}' updated for ID:`, leagueId);
    return true;
  } catch (error) {
    console.error("Error updating league field:", error);
  }
};

/**
 * Checks if a team with the given name already exists in the league.
 * @param leagueId The Firestore document ID of the league.
 * @param teamName The team name to check.
 * @returns true if the team exists, false otherwise
 */
export const doesTeamExist = async (
  leagueId: string,
  teamName: string
): Promise<boolean> => {
  try {
    const leagueDocRef = doc(db, LEAGUE_COLLECTION, leagueId);
    const snap = await getDoc(leagueDocRef);

    if (!snap.exists()) {
      console.warn("League not found:", leagueId);
      return false;
    }

    const data = snap.data();
    const teams: any[] = data.teams || [];

    return teams.some((t) => t.name === teamName);
  } catch (error) {
    console.error("Error checking team existence:", error);
    return false;
  }
};

/**
 * Partially adds/updates a league document with the 'teams' key.
 * @param leagueId The Firestore document ID of the league.
 * @param team The team object to add if teamName is not provided.
 * @param teamName If provided, updates the specific team's isDuePaid field.
 */

export const updateLeagueTeams = async (
  leagueId: string,
  team: { name: string; createdBy: string; [key: string]: any } | {},
  teamName?: string,
  isUpdatePayment = true
): Promise<{ success: boolean; message: string }> => {
  try {
    const leagueDocRef = doc(db, LEAGUE_COLLECTION, leagueId);

    if (teamName && isUpdatePayment) {
      /**
       * Case 1: Toggle isDuePaid for an existing team
       */
      const snap = await getDoc(leagueDocRef);
      if (snap.exists()) {
        const data = snap.data();
        const teams: any[] = data.teams || [];

        const updatedTeams = teams.map((t) =>
          t.name === teamName ? { ...t, isDuePaid: !t.isDuePaid } : t
        );

        await updateDoc(leagueDocRef, { teams: updatedTeams });
        return {
          success: true,
          message: `Team '${teamName}' updated for league ${leagueId}`,
        };
      } else {
        return { success: false, message: "League not found" };
      }
    } else if (teamName && !isUpdatePayment) {
      const snap = await getDoc(leagueDocRef);
      if (snap.exists()) {
        const data = snap.data();
        const teams: any[] = data.teams || [];

        // ✅ Check if team already exists
        // Allow if editing itself
        if (
          teams.some(
            (t) => t.name === (team as any).name && t.name !== teamName
          )
        ) {
          return { success: false, message: "Team name exists" };
        }

        const updatedTeams = teams.map((t) => (t.name === teamName ? team : t));
        await updateDoc(leagueDocRef, { teams: updatedTeams });
        return {
          success: true,
          message: `Team '${teamName}' updated for league ${leagueId}`,
        };
      } else {
        return { success: false, message: "League not found" };
      }
    } else {
      /**
       * Case 2: Add a new team with validations
       */
      const snap = await getDoc(leagueDocRef);
      if (!snap.exists()) {
        return { success: false, message: "League not found" };
      }

      const data = snap.data();
      const teams: any[] = data.teams || [];
      const leagueSize = Number(data.leagueSize || 0);

      // ✅ Check if team already exists
      if (teams.some((t) => t.createdBy === (team as any).createdBy)) {
        return { success: false, message: "User already exists" };
      }

      if (teams.some((t) => t.name === (team as any).name)) {
        //return { success: false, message: "Team already exists" };
        // instead of return false if the team name exists, append a random number to the team name by appending a random 4 digit number
        const randomNum = Math.floor(1000 + Math.random() * 9000);
        (team as any).name = `${(team as any).name}-${randomNum}`;
      }

      // ✅ Check if league is full
      if (teams.length >= leagueSize) {
        return { success: false, message: "All team slots are filled" };
      }

      // Add new team
      await updateDoc(leagueDocRef, {
        teams: arrayUnion(team),
      });

      // Add userLeagues entry
      if (
        typeof team === "object" &&
        team !== null &&
        "createdBy" in team &&
        typeof (team as any).createdBy === "string"
      ) {
        await addUserLeague((team as any).createdBy, leagueId);
      } else {
        console.warn(
          "Team object does not have a valid 'createdBy' property:",
          team
        );
      }

      return { success: true, message: `New team added to league ${leagueId}` };
    }
  } catch (error) {
    console.error("Error updating team:", error);
    return { success: false, message: "Error updating team" };
  }
};

export const updateLeagueBasicInfo = async (
  leagueId: string,
  data: {
    leagueName?: string;
    leagueUrl?: string;
    leagueSize?: string;
    platform?: string;
    leagueBio?: string | null;
    bannerImage?: string | null;
    tiktok?: string | null;
    youtube?: string | null;
    facebook?: string | null;
    instagram?: string | null;
    x?: string | null;
    description?: string | null;
    createdAt?: string;
    isPublic?: boolean;
    merchandise?: {
      productId: string;
      productTitle: string;
      productImage: string;
      productUrl: string;
    }[];
    teams?: Team[];
  }
) => {
  try {
    const leagueDocRef = doc(db, LEAGUE_COLLECTION, leagueId);
    // Only update the allowed fields
    const allowedFields = [
      "leagueName",
      "leagueUrl",
      "leagueSize",
      "platform",
      "bannerImage",
      "isPublic",
      "leagueBio",
      "tiktok",
      "youtube",
      "facebook",
      "instagram",
      "x",
      "description",
      "merchandise",
      "teams",
    ] as const;

    const updateData: Partial<typeof data> = {};
    for (const key of allowedFields) {
      if (data[key] !== undefined) {
        updateData[key] = data[key] as any;
      }
    }
    await updateDoc(leagueDocRef, updateData);
    console.log(updateData);
    console.log("League basic info updated for ID:", leagueId);
    return true;
  } catch (error) {
    console.error("Error updating league basic info:", error);
    throw error;
  }
};

export const updateLeagueFundsDistribution = async (
  leagueId: string,
  distribution: { place: string; percentage: number; amount: number }[],
  totalPot: number
) => {
  try {
    const leagueDocRef = doc(db, LEAGUE_COLLECTION, leagueId);
    await updateDoc(leagueDocRef, {
      "leagueFunds.distribution": distribution,
      "leagueFunds.totalPot": totalPot,
    });
    console.log(
      "League funds distribution and totalPot updated for ID:",
      leagueId
    );
    return true;
  } catch (error) {
    console.error("Error updating league funds distribution:", error);
    throw error;
  }
};

export const getAllLeaguesByUserId = async (
  userId: string
): Promise<DocumentData[]> => {
  try {
    const q = query(
      collection(db, LEAGUE_COLLECTION),
      where("createdBy", "==", userId)
    );

    const snapshot = await getDocs(q);
    const leagues: DocumentData[] = [];

    snapshot.forEach((doc) => {
      leagues.push({ id: doc.id, ...doc.data() });
    });

    // ✅ Sort: first by createdAt (if present), else by leagueName
    leagues.sort((a, b) => {
      if (a.createdAt && b.createdAt) {
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      }
      if (a.createdAt && !b.createdAt) return -1;
      if (!a.createdAt && b.createdAt) return 1;
      return a.leagueName.localeCompare(b.leagueName);
    });

    return leagues;
  } catch (error) {
    console.error("Error fetching leagues by user ID:", error);
    throw error;
  }
};

/**
 *
 * @param userId
 * @param leagueId
 * @returns
 */

export const addUserLeague = async (userId: string, leagueId: string) => {
  try {
    const userDocRef = doc(db, "userLeagues", userId);
    // Use setDoc with merge to create or update, and arrayUnion to avoid duplicates
    await setDoc(
      userDocRef,
      { leagues: arrayUnion(leagueId) },
      { merge: true }
    );
    console.log(`League ${leagueId} added for user ${userId}`);
    return true;
  } catch (error) {
    console.error("Error adding league to user:", error);
    throw error;
  }
};

export const getUserDetails = async (
  userId: string
): Promise<DocumentData | null> => {
  try {
    const docRef = doc(db, USER_COLLECTION, userId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      console.log("No such document!");
      return null;
    }
  } catch (error) {
    console.error("Error fetching user details:", error);
    throw error;
  }
};

export const getUserLeagues = async (
  userId: string
): Promise<DocumentData[]> => {
  try {
    const userDocRef = doc(db, "userLeagues", userId);
    const userSnap = await getDoc(userDocRef);

    if (!userSnap.exists()) {
      console.log("No leagues found for user:", userId);
      return [];
    }

    const leagueIds: string[] = userSnap.get("leagues") || [];
    if (leagueIds.length === 0) {
      return [];
    }

    // Fetch league details in a single query using 'in' operator
    const q = query(
      collection(db, LEAGUE_COLLECTION),
      where("__name__", "in", leagueIds)
    );

    const snapshot = await getDocs(q);
    const leagues: DocumentData[] = [];

    snapshot.forEach((doc) => {
      leagues.push({ id: doc.id, ...doc.data() });
    });

    // Sort leagues by createdAt (if present), else by leagueName
    leagues.sort((a, b) => {
      if (a.createdAt && b.createdAt) {
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      }
      if (a.createdAt && !b.createdAt) return -1;
      if (!a.createdAt && b.createdAt) return 1;
      return a.leagueName.localeCompare(b.leagueName);
    });

    return leagues;
  } catch (error) {
    console.error("Error fetching user leagues:", error);
    throw error;
  }
};

export const updateLeagueAboutValue = async (leagueId: string, data: any) => {
  try {
    const leagueDocRef = doc(db, LEAGUE_COLLECTION, leagueId);
    await updateDoc(leagueDocRef, {
      about: data.about,
    });
    console.log(data);
    return true;
  } catch (error) {
    console.error("Error updating league field:", error);
  }
};

export const updateLeagueDescriptionValue = async (
  leagueId: string,
  data: any
) => {
  try {
    const leagueDocRef = doc(db, LEAGUE_COLLECTION, leagueId);
    await updateDoc(leagueDocRef, {
      description: data.description,
      tiktok: data.tiktok,
      youtube: data.youtube,
      facebook: data.facebook,
      instagram: data.instagram,
      x: data.x,
    });
    console.log(data);
    return true;
  } catch (error) {
    console.error("Error updating league field:", error);
  }
};

export const addLeagueFollower = async (
  leagueId: string,
  follower: { uid: string; email: string }
) => {
  try {
    const leagueFollowersDocRef = doc(db, FOLLOWER_COLLECTION, leagueId);
    const userDocRef = doc(db, USER_COLLECTION, follower.uid);
    // Ensure doc exists (set if not exists)
    await setDoc(
      leagueFollowersDocRef,
      {
        followers: arrayUnion(follower),
      },
      { merge: true } // merge ensures we don’t overwrite the document
    );

    await setDoc(
      userDocRef,
      {
        followedLeagues: arrayUnion(leagueId),
      },
      { merge: true }
    );

    console.log("Follower added:", follower);
    return true;
  } catch (error) {
    console.error("Error adding follower:", error);
    return false;
  }
};

export const removeLeagueFollower = async (
  leagueId: string,
  follower: { uid: string; email: string }
) => {
  try {
    const leagueFollowersDocRef = doc(db, FOLLOWER_COLLECTION, leagueId);
    const userDocRef = doc(db, USER_COLLECTION, follower.uid);

    // 1. Remove follower from leagueFollowers
    await setDoc(
      leagueFollowersDocRef,
      {
        followers: arrayRemove(follower),
      },
      { merge: true }
    );

    // 2. Remove leagueId from user's followedLeagues
    await setDoc(
      userDocRef,
      {
        followedLeagues: arrayRemove(leagueId),
      },
      { merge: true }
    );

    console.log(`Follower ${follower.uid} removed from league ${leagueId}`);
    return true;
  } catch (error) {
    console.error("Error removing league follower:", error);
    return false;
  }
};

export const hasUserFollowedLeague = async (
  userId: string,
  leagueId: string
): Promise<boolean> => {
  try {
    const userDocRef = doc(db, USER_COLLECTION, userId);
    const snapshot = await getDoc(userDocRef);

    if (!snapshot.exists()) {
      return false; // user doc not found
    }

    const data = snapshot.data();
    const followedLeagues: string[] = data.followedLeagues || [];

    return followedLeagues.includes(leagueId);
  } catch (error) {
    console.error("Error checking if user follows league:", error);
    return false;
  }
};

export const addLeagueFeed = async (
  leagueId: string,
  feedData: { [key: string]: any },
  feedId?: string
) => {
  try {
    const now = new Date().toISOString();
    const feedsColRef = collection(
      db,
      LEAGUE_FEEDS_COLLECTION,
      leagueId,
      "feeds"
    );

    if (feedId) {
      // Edit existing feed (or create if not present) using setDoc with merge
      const feedDocRef = doc(
        db,
        LEAGUE_FEEDS_COLLECTION,
        leagueId,
        "feeds",
        feedId
      );
      await setDoc(
        feedDocRef,
        { ...feedData, leagueId, updatedAt: now },
        { merge: true }
      );
      console.log("Feed updated with ID:", feedId);
      return { id: feedId, ...feedData, leagueId, updatedAt: now };
    } else {
      // Add new feed
      const docRef = await addDoc(feedsColRef, {
        ...feedData,
        createdAt: now,
      });
      console.log("Feed added with ID:", docRef.id);
      return { id: docRef.id, ...feedData, leagueId, createdAt: now };
    }
  } catch (error) {
    console.error("Error adding/updating league feed:", error);
    throw error;
  }
};

export const updateLikeCountOnFeed = async (
  leagueId: string,
  feedId: string,
  userId: string
): Promise<{ success: boolean; message?: string; likeCounter?: number }> => {
  try {
    const feedDocRef = doc(
      db,
      LEAGUE_FEEDS_COLLECTION,
      leagueId,
      "feeds",
      feedId
    );

    const snap = await getDoc(feedDocRef);
    if (!snap.exists()) {
      console.warn("Feed not found:", feedId);
      return { success: false, message: "Feed not found" };
    }

    const data = snap.data() || {};
    const likedBy: string[] = data.likedBy || [];
    const currentCount =
      typeof data.likeCounter === "number" ? data.likeCounter : 0;

    if (likedBy.includes(userId)) {
      // User already liked -> remove like
      const newCount = Math.max(0, currentCount - 1);
      await updateDoc(feedDocRef, {
        likeCounter: increment(-1),
        likedBy: arrayRemove(userId),
      });
      return { success: true, likeCounter: newCount };
    }

    // User hasn't liked -> add like
    await updateDoc(feedDocRef, {
      likeCounter: increment(1),
      likedBy: arrayUnion(userId),
    });

    return { success: true, likeCounter: currentCount + 1 };
  } catch (error) {
    console.error("Error toggling like on feed:", error);
    return { success: false, message: "Error updating like" };
  }
};

export const deleteLeagueFeed = async (
  leagueId: string,
  feedId: string
): Promise<{ success: boolean; message?: string }> => {
  try {
    const feedDocRef = doc(
      db,
      LEAGUE_FEEDS_COLLECTION,
      leagueId,
      "feeds",
      feedId
    );

    const snap = await getDoc(feedDocRef);
    if (!snap.exists()) {
      console.warn("Feed not found:", feedId);
      return { success: false, message: "Feed not found" };
    }
    await deleteDoc(feedDocRef);

    console.log("Feed deleted with ID:", feedId);
    return { success: true, message: "Feed deleted" };
  } catch (error) {
    console.error("Error deleting league feed:", error);
    return { success: false, message: "Error deleting feed" };
  }
};

/**
 * Fetch a single feed document by ID from the league's feeds subcollection
 * @param leagueId - the parent league document id
 * @param feedId - the feed document id
 */
export const getFeedById = async (
  leagueId: string,
  feedId: string
): Promise<DocumentData | null> => {
  try {
    const feedDocRef = doc(
      db,
      LEAGUE_FEEDS_COLLECTION,
      leagueId,
      "feeds",
      feedId
    );
    const snap = await getDoc(feedDocRef);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (error) {
    console.error("Error fetching feed by id:", error);
    throw error;
  }
};

/**
 * Update the `leagueRules` field of a league document in Firestore.
 *
 * Replaces the existing `leagueRules` value for the league identified by `leagueId`
 * with the provided `payload`. The payload should be a plain object with values
 * supported by Firestore (e.g., primitives, arrays, nested objects, Timestamps, etc.).
 *
 * @param leagueId - The Firestore document ID of the league to update.
 * @param payload - An object containing the new league rules to set on the document.
 *                  This object will be written to the `leagueRules` field.
 * @returns A promise that resolves to `true` when the update completes successfully.
 * @throws Will rethrow any error that occurs while attempting to update the document
 *         (for example, network errors or permission/validation failures).
 *
 * @example
 * ```ts
 * await updateLeagueRules('league123', { draftType: 'snake', maxTeams: 12 });
 * ```
 */
export const updateLeagueRules = async (
  leagueId: string,
  leagueRules: object
) => {
  try {
    const leagueDocRef = doc(db, LEAGUE_COLLECTION, leagueId);
    await updateDoc(leagueDocRef, {
      leagueRules: leagueRules,
    });
    console.log("League rules updated for ID:", leagueId);
    return true;
  } catch (error) {
    console.error("Error updating league rules:", error);
    throw error;
  }
};

export const addMerchandiseToLeague = async (
  leagueId: string,
  merchandise: {
    productId: string;
    productTitle: string;
    productImage: string;
    productUrl: string;
  }
) => {
  try {
    const leagueDocRef = doc(db, LEAGUE_COLLECTION, leagueId);
    const snap = await getDoc(leagueDocRef);

    // Determine current max displayOrder
    let maxDisplayOrder = 0;
    if (snap.exists()) {
      const data = snap.data();
      const merchArray = Array.isArray(data?.merchandise)
        ? data.merchandise
        : [];
      maxDisplayOrder = merchArray.reduce((max, item) => {
        const order =
          typeof item?.displayOrder === "number" ? item.displayOrder : 0;
        return Math.max(max, order);
      }, 0);
    }

    const newMerch = { ...merchandise, displayOrder: maxDisplayOrder + 1 };

    // If league doc exists update, otherwise create/merge
    if (snap.exists()) {
      await updateDoc(leagueDocRef, {
        merchandise: arrayUnion(newMerch),
      });
    } else {
      await setDoc(
        leagueDocRef,
        {
          merchandise: arrayUnion(newMerch),
        },
        { merge: true }
      );
    }

    console.log(
      "Merchandise added to league ID:",
      leagueId,
      "with displayOrder:",
      newMerch.displayOrder
    );
    return true;
  } catch (error) {
    console.error("Error adding merchandise to league:", error);
    throw error;
  }
};

/**
 * Fetch the merchandise array for a given league document.
 * Returns an empty array if the league or merchandise field doesn't exist.
 * Merchandise items are sorted by `displayOrder` ascending when present.
 */
export const getMerchandises = async (
  leagueId: string
): Promise<DocumentData[]> => {
  try {
    const leagueDocRef = doc(db, LEAGUE_COLLECTION, leagueId);
    const snap = await getDoc(leagueDocRef);

    if (!snap.exists()) {
      console.warn("League not found when fetching merchandise:", leagueId);
      return [];
    }

    const data = snap.data();
    const merchArray: any[] = Array.isArray(data?.merchandise)
      ? data.merchandise
      : [];

    merchArray.sort((a: any, b: any) => {
      const aOrder =
        typeof a?.displayOrder === "number"
          ? a.displayOrder
          : Number.MAX_SAFE_INTEGER;
      const bOrder =
        typeof b?.displayOrder === "number"
          ? b.displayOrder
          : Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });

    return merchArray;
  } catch (error) {
    console.error("Error fetching merchandises for league:", leagueId, error);
    throw error;
  }
};

export const updateCommissionerTeamDetails = async (
  leagueId: string,
  userId: string,
  commissionerTeamDetails: Record<string, any>
): Promise<boolean> => {
  try {
    const leagueDocRef = doc(db, LEAGUE_COLLECTION, leagueId);
    const snap = await getDoc(leagueDocRef);
    if (!snap.exists()) {
      console.warn("League not found:", leagueId);
      return false;
    }
    const teams: any[] = snap.data().teams || [];
    if (!teams.some((team) => team.createdBy === userId)) {
      console.warn("Commissioner team not found for user:", userId);
      return false;
    }
    const updatedTeams = teams.map((team) =>
      team.createdBy === userId ? { ...team, ...commissionerTeamDetails } : team
    );
    await updateDoc(leagueDocRef, { teams: updatedTeams });
    return true;
  } catch (error) {
    console.error("Error updating commissioner team details:", error);
    throw error;
  }
};

export const updateMerchandiseToLeague = async (
  leagueId: string,
  productId: string | number,
  updates: {
    productTitle?: string;
    productImage?: string;
    productUrl?: string;
  }
) => {
  try {
    const leagueDocRef = doc(db, LEAGUE_COLLECTION, leagueId);
    const snap = await getDoc(leagueDocRef);
    if (!snap.exists()) {
      console.warn("League not found:", leagueId);
      return false;
    }
    const data = snap.data();
    const merchArray: any[] = Array.isArray(data?.merchandise)
      ? data.merchandise
      : [];
    console.log("Current merchandise array:", merchArray);
    console.log("Updating productId:", productId, "with updates:", updates);
    const merchIndex = merchArray.findIndex((m) => m.productId === productId);
    if (merchIndex === -1) {
      console.warn("Merchandise not found:", productId);
      return false;
    }
    const updatedMerch = { ...merchArray[merchIndex], ...updates };
    const newMerchArray = [
      ...merchArray.slice(0, merchIndex),
      updatedMerch,
      ...merchArray.slice(merchIndex + 1),
    ];
    await updateDoc(leagueDocRef, { merchandise: newMerchArray });
    return true;
  } catch (error) {
    console.error("Error updating merchandise:", error);
    throw error;
  }
};

export const updateLeagueProgressStep = async (
  leagueId: string,
  leagueCompletedStepId: string
) => {
  console.log(
    "Updating league progress steps with payload:",
    leagueCompletedStepId
  );
  let leagueCompletedSteps = [leagueCompletedStepId];
  try {
    const leagueDocRef = doc(db, LEAGUE_COLLECTION, leagueId);
    // Fetch existing completed steps to avoid overwriting
    const snap = await getDoc(leagueDocRef);
    if (snap.exists()) {
      const data = snap.data();
      // proceed only if leagueCompletedStepId is already present
      if (data.leagueProgressTracker === true) {
        const existingSteps: string[] = data.leagueCompletedSteps || [];
        leagueCompletedSteps.push(
          ...existingSteps.filter((step) => step !== leagueCompletedStepId)
        );
        await updateDoc(leagueDocRef, {
          leagueCompletedSteps: leagueCompletedSteps,
        });
        console.log(
          `League progress step '${leagueCompletedSteps}' updated for league ID:`,
          leagueId
        );
      }
    }
    return true;
  } catch (error) {
    console.error("Error updating league progress steps:", error);
    throw error;
  }
};

export const updateLeagueProgressTrackerStatus = async (
  leagueId: string,
  value: boolean
) => {
  //placeholder for future use
  try {
    const leagueDocRef = doc(db, LEAGUE_COLLECTION, leagueId);
    await updateDoc(leagueDocRef, { leagueProgressTracker: value });
    console.log(
      `League progress tracker updated for league ID: ${leagueId} to value: ${value}`
    );
    return true;
  } catch (error) {
    console.error("Error updating league progress tracker:", error);
    throw error;
  }
};

export const deleteLeagueTeams = async (
  leagueId: string,
  teams: Team[],
  userIdForTeamToDelete: string
): Promise<boolean> => {
  try {
    const leagueDocRef = doc(db, LEAGUE_COLLECTION, leagueId);
    const userLeaguesDocRef = doc(
      db,
      USER_LEAGUES_COLLECTION,
      userIdForTeamToDelete
    );
    await updateDoc(leagueDocRef, { teams: teams, isPublic: false });
    await updateDoc(userLeaguesDocRef, {
      leagues: arrayRemove(leagueId),
    });
    console.log(`Teams updated for league ID: ${leagueId}`);
    return true;
  } catch (error) {
    console.error("Error updating teams in league:", error);
    return false;
  }
};

export const getSeasonSchedule = async () => {
  const currentYear = new Date().getFullYear().toString();
  const scheduleDocRef = doc(db, "seasonSchedule", currentYear);
  const snap = await getDoc(scheduleDocRef);
  if (!snap.exists()) {
    console.warn("Season schedule not found for year:", currentYear);
    return null;
  }
  return snap.data();
};

// -------- DO NOT DELETE - Used to add season schedule to Firestore -----------

// export const addScheduleToFirestore = async () => {
//   try {
//     const scheduleData = {
//       season: {
//         seasonStartDate: "2025-08-26",
//         seasonEndDate: "2026-01-06",
//       },
//       weeks: [
//         {
//           weekID: 1,
//           weekName: "Week 1",
//           weekStartDate: "2025-09-04",
//           weekEndDate: "2025-09-08",
//         },
//         {
//           weekID: 2,
//           weekName: "Week 2",
//           weekStartDate: "2025-09-11",
//           weekEndDate: "2025-09-15",
//         },
//         {
//           weekID: 3,
//           weekName: "Week 3",
//           weekStartDate: "2025-09-18",
//           weekEndDate: "2025-09-22",
//         },
//         {
//           weekID: 4,
//           weekName: "Week 4",
//           weekStartDate: "2025-09-25",
//           weekEndDate: "2025-09-29",
//         },
//         {
//           weekID: 5,
//           weekName: "Week 5",
//           weekStartDate: "2025-10-02",
//           weekEndDate: "2025-10-06",
//         },
//         {
//           weekID: 6,
//           weekName: "Week 6",
//           weekStartDate: "2025-10-09",
//           weekEndDate: "2025-10-13",
//         },
//         {
//           weekID: 7,
//           weekName: "Week 7",
//           weekStartDate: "2025-10-16",
//           weekEndDate: "2025-10-20",
//         },
//         {
//           weekID: 8,
//           weekName: "Week 8",
//           weekStartDate: "2025-10-23",
//           weekEndDate: "2025-10-27",
//         },
//         {
//           weekID: 9,
//           weekName: "Week 9",
//           weekStartDate: "2025-10-30",
//           weekEndDate: "2025-11-03",
//         },
//         {
//           weekID: 10,
//           weekName: "Week 10",
//           weekStartDate: "2025-11-06",
//           weekEndDate: "2025-11-10",
//         },
//         {
//           weekID: 11,
//           weekName: "Week 11",
//           weekStartDate: "2025-11-13",
//           weekEndDate: "2025-11-17",
//         },
//         {
//           weekID: 12,
//           weekName: "Week 12",
//           weekStartDate: "2025-11-20",
//           weekEndDate: "2025-11-24",
//         },
//         {
//           weekID: 13,
//           weekName: "Week 13",
//           weekStartDate: "2025-11-27",
//           weekEndDate: "2025-12-01",
//         },
//         {
//           weekID: 14,
//           weekName: "Week 14",
//           weekStartDate: "2025-12-04",
//           weekEndDate: "2025-12-08",
//         },
//         {
//           weekID: 15,
//           weekName: "Week 15",
//           weekStartDate: "2025-12-11",
//           weekEndDate: "2025-12-15",
//         },
//         {
//           weekID: 16,
//           weekName: "Week 16",
//           weekStartDate: "2025-12-18",
//           weekEndDate: "2025-12-22",
//         },
//         {
//           weekID: 17,
//           weekName: "Week 17",
//           weekStartDate: "2025-12-25",
//           weekEndDate: "2025-12-29",
//         },
//         {
//           weekID: 18,
//           weekName: "Week 18",
//           weekStartDate: "2025-12-30",
//           weekEndDate: "2026-01-06",
//         },
//       ],
//     };

//     const scheduleDocRef = doc(db, "seasonSchedule", "2025");
//     await setDoc(scheduleDocRef, scheduleData);

//     console.log("2025 season schedule added to Firestore successfully");
//     return { success: true, message: "Schedule updated successfully" };
//   } catch (error) {
//     console.error("Error adding schedule to Firestore:", error);
//     throw error;
//   }
// };

// --------------------------------------------------------------------------------------

export const createOrUpdateSeasonChallenge = async (
  challengeData: any,
  challengeId?: string
): Promise<{ success: boolean; message: string; challengeId?: string }> => {
  console.log("Challenge Data:", challengeData);
  try {
    if (!challengeData || !challengeData.leagueId) {
      throw new Error(
        "leagueId is required in challengeData for createOrUpdateSeasonChallenge"
      );
    }
    if (challengeId) {
      // Update existing challenge
      const challengeDocRef = doc(
        db,
        LEAGUE_COLLECTION,
        challengeData.leagueId,
        CHALLENGE_COLLECTION,
        challengeId
      );
      await setDoc(challengeDocRef, challengeData, { merge: true });
      console.log("Season challenge updated successfully");
      return {
        success: true,
        message: "Challenge updated successfully",
        challengeId,
      };
    } else {
      // Create new challenge
      const challengesColRef = collection(
        db,
        LEAGUE_COLLECTION,
        challengeData.leagueId,
        CHALLENGE_COLLECTION
      );
      const docRef = await addDoc(challengesColRef, challengeData);
      const newId = docRef.id;
      console.log("Season challenge created successfully with ID:", newId);
      return {
        success: true,
        message: "Challenge created successfully",
        challengeId: newId,
      };
    }
  } catch (error) {
    console.error("Error adding/updating challenge to Firestore:", error);
    throw error;
  }
};

export const getChallengeDetails = async (
  leagueId: string,
  challengeId: string
): Promise<DocumentData | null> => {
  try {
    const challengeDocRef = doc(
      db,
      LEAGUE_COLLECTION,
      leagueId,
      CHALLENGE_COLLECTION,
      challengeId
    );
    const snap = await getDoc(challengeDocRef);
    if (!snap.exists()) {
      console.warn("Challenge not found:", challengeId);
      return null;
    }
    return { id: snap.id, ...snap.data() };
  } catch (error) {
    console.error("Error fetching challenge details:", error);
    throw error;
  }
};

/**
 * Update a single team inside a challenge's teams array.
 * @param leagueId Firestore ID of the league.
 * @param challengeId Firestore ID of the challenge.
 * @param updatedTeam Team object with at least a createdBy identifier; merged into existing team.
 */
export const updateChallengeTeams = async (
  leagueId: string,
  challengeId: string,
  updatedTeam: Record<string, any>
): Promise<{ success: boolean; message?: string }> => {
  try {
    if (!updatedTeam || !updatedTeam.createdBy) {
      return { success: false, message: "Team missing createdBy" };
    }
    const challengeDocRef = doc(
      db,
      LEAGUE_COLLECTION,
      leagueId,
      CHALLENGE_COLLECTION,
      challengeId
    );
    const snap = await getDoc(challengeDocRef);
    if (!snap.exists()) {
      console.warn("Challenge not found while updating team:", challengeId);
      return { success: false, message: "Challenge not found" };
    }
    const data = snap.data() || {};
    const existingTeams: any[] = Array.isArray(data.teams) ? data.teams : [];
    const idx = existingTeams.findIndex(
      (t) => t.createdBy === updatedTeam.createdBy
    );
    if (idx === -1) {
      console.warn(
        "Team to update not found in challenge teams array",
        updatedTeam.createdBy
      );
      return { success: false, message: "Team not found" };
    }
    existingTeams[idx] = { ...existingTeams[idx], ...updatedTeam };
    await updateDoc(challengeDocRef, { teams: existingTeams });
    console.log(
      `Challenge team (createdBy=${updatedTeam.createdBy}) updated for challenge ID: ${challengeId}`
    );
    return { success: true, message: "Challenge team updated successfully" };
  } catch (error) {
    console.error("Error updating challenge team:", error);
    return { success: false, message: "Error updating challenge team" };
  }
};

/**
 * Soft deletes a challenge document from the CHALLENGE_COLLECTION subcollection of a league.
 * Sets the 'challengeCreationStatus' field to 'deleted' instead of removing the document.
 * @param leagueId The Firestore document ID of the league.
 * @param challengeId The Firestore document ID of the challenge to soft delete.
 * @returns A promise that resolves to an object indicating success or failure.
 */
export const deleteChallenge = async (
  leagueId: string,
  challengeId: string
): Promise<{ success: boolean; message?: string }> => {
  try {
    const challengeDocRef = doc(
      db,
      LEAGUE_COLLECTION,
      leagueId,
      CHALLENGE_COLLECTION,
      challengeId
    );
    await updateDoc(challengeDocRef, { challengeCreationStatus: "deleted" });
    console.log(
      `Challenge soft deleted (challengeCreationStatus: "deleted") with ID: ${challengeId} from league: ${leagueId}`
    );
    return { success: true, message: "Challenge soft deleted" };
  } catch (error) {
    console.error("Error soft deleting challenge:", error);
    return { success: false, message: "Error soft deleting challenge" };
  }
};

/**
 * Update a feed document in the leagueLockerFeed collection by searching for challengeId field.
 * @param leagueId The Firestore document ID of the league.
 * @param challengeId The challengeId field value to search for.
 * @param updateData The new values to update in the feed document.
 * @returns A promise that resolves to an object indicating success or failure.
 */
export const updateLeagueFeedByChallengeField = async (
  leagueId: string,
  challengeId: string,
  updateData: { [key: string]: any }
): Promise<{ success: boolean; message?: string }> => {
  try {
    const feedsColRef = collection(
      db,
      LEAGUE_FEEDS_COLLECTION,
      leagueId,
      "feeds"
    );
    const q = query(feedsColRef, where("challengeId", "==", challengeId));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.warn(
        `No feed found for leagueId: ${leagueId} with challengeId: ${challengeId}`
      );
      return { success: false, message: "Feed not found for challengeId" };
    }

    // Merge update for all matching feeds (usually should be one)
    for (const feedDoc of snapshot.docs) {
      await setDoc(feedDoc.ref, updateData, { merge: true });
      console.log(
        `Feed merged for leagueId: ${leagueId}, feedId: ${feedDoc.id}, challengeId: ${challengeId}`,
        updateData
      );
    }

    return { success: true, message: "Feed(s) updated successfully" };
  } catch (error) {
    console.error("Error updating feed by challengeId field:", error);
    return { success: false, message: "Error updating feed" };
  }
};
